import { useMutation, UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import useApi from "../../../services/auth/useApi";
import { AxiosError } from "axios";

import { inProgressSpaceListState } from "../../../recoil/spaces/inProgressSpaceListState";
import { useRecoilState } from "recoil";
import { SpaceIdType } from "../../../types/space";
import { SpaceStatus } from "../../../types/space/enum";
import { spaceQueryKeys } from "../queries";
import { useFetchArchivedSpaceQuery } from "../core/useFetchSpaceQuery";
import { usePreference } from "../../../hooks/preference/usePreference";

export const markSpaceUndoneApi = async (
  api: ReturnType<typeof useApi>,
  projectId: SpaceIdType
): Promise<void> => {
  await api.patch(`/spaces/${projectId}`, { itemStatus: SpaceStatus.InProgress });
};

export const useMarkSpaceUndoneMutation = (
  options?: UseMutationOptions<void, AxiosError, SpaceIdType>
) => {
  const api = useApi();
  const queryClient = useQueryClient();
  const [projectList, setProjectList] = useRecoilState(inProgressSpaceListState);
  const { data: archivedSpaceList } = useFetchArchivedSpaceQuery();
  const { preference, updatePreference } = usePreference();

  return useMutation<void, AxiosError, SpaceIdType>({
    mutationFn: (projectId: SpaceIdType) => markSpaceUndoneApi(api, projectId),
    onMutate: async (projectId) => {
      await queryClient.cancelQueries({ queryKey: spaceQueryKeys.status(SpaceStatus.Completed) });
      await queryClient.cancelQueries({ queryKey: spaceQueryKeys.status(SpaceStatus.InProgress) });

      const currentSpace = archivedSpaceList?.find((space) => space.id === projectId);
      if (!currentSpace) return;

      const statusChangedSpace = {
        ...currentSpace,
        itemStatus: SpaceStatus.InProgress,
      };

      setProjectList((prev) => {
        if (!prev) return [statusChangedSpace];
        return [...prev, statusChangedSpace];
      });

      updatePreference({ selectedSpaceIds: [...preference?.selectedSpaceIds, projectId] });

      return { projectId, previousProjectList: projectList };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceQueryKeys.status(SpaceStatus.Completed) });
      queryClient.invalidateQueries({ queryKey: spaceQueryKeys.status(SpaceStatus.InProgress) });
    },
    ...options,
  });
};
